import React, { useEffect, useState } from 'react';
import { X, CaretDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DS_SPACING } from '@/config/ui-invariants';
import { INTENT_TO_BUTTON, IDEA_QUEUE_LABELS } from '@/config/task-display-config';
import { getIdeaCardRejectReasons } from '@/lib/api';

/**
 * The pre-draft IDEA CARD's reject vocabulary — ONE fetcher, ONE menu, TWO readers.
 *
 * WHY THIS MODULE EXISTS (WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001, item B).
 * An idea card speaks its own seven-code vocabulary (IDEA_CARD_CONTRACT §4), which is
 * DISJOINT from the draft-stage reject taxonomy — no code appears in both. The Work Inbox
 * card fetched the right one; the Post Studio Ideas tab reused the DRAFT-stage menu under a
 * comment claiming "the SAME menu the other tabs use — one vocabulary, not two". The claim
 * was inverted: there ARE two vocabularies, and the tab was offering the wrong one, so the
 * backend refused every reject fired from it with a 400 (`work_inbox.py`, the
 * IDEA_CARD_REJECT_CODES guard). Four cards the CEO wanted to clear could not be cleared.
 *
 * The fix is not a second list — it is ONE home both surfaces read, which is the pattern the
 * estate already uses everywhere else (one producer, many readers). The codes and their
 * two-line definitions live on the backend in `linkedin_post_rules.json`
 * ::idea_card_reject_taxonomy and arrive over
 * `GET /api/linkedin-posts/idea-card-reject-reasons`. Nothing here hardcodes a code, a label
 * or a definition — a component that types its own copy of a vocabulary is how the two
 * surfaces drifted apart in the first place.
 */

// Module-level memoised fetch — the taxonomy is static config, so N mounted cards share ONE
// request. Moved here verbatim from LinkedInPostDetail.jsx, which was its only home.
let ideaCardRejectTaxonomyPromise = null;

export function fetchIdeaCardRejectTaxonomyOnce() {
    if (!ideaCardRejectTaxonomyPromise) {
        // Promise.resolve() wraps the call so a client that returns a non-promise cannot throw
        // synchronously and take the whole page down with it. This menu now mounts on the Post
        // Studio Ideas tab as well as the Work Inbox card, so a fault here would blank a screen
        // rather than one card. An empty menu is the correct degradation; a crash is not.
        ideaCardRejectTaxonomyPromise = Promise.resolve()
            .then(() => getIdeaCardRejectReasons())
            .catch((err) => {
                ideaCardRejectTaxonomyPromise = null;
                throw err;
            });
    }
    return ideaCardRejectTaxonomyPromise;
}

// Test seam only — resets the memo so a suite can assert the fetch happens per case.
export function __resetIdeaCardRejectTaxonomyForTests() {
    ideaCardRejectTaxonomyPromise = null;
}

/**
 * The codes, fetched once, shared by every caller. Returns [] until it resolves and [] on
 * failure — an empty menu, never a crash, and never a silent fallback to the WRONG
 * vocabulary, which is the defect this module exists to end.
 */
export function useIdeaCardRejectCodes() {
    const [codes, setCodes] = useState([]);
    useEffect(() => {
        let cancelled = false;
        fetchIdeaCardRejectTaxonomyOnce()
            .then((data) => { if (!cancelled) setCodes(data?.codes || []); })
            .catch(() => { /* empty default — the menu shows nothing rather than crashing */ });
        return () => { cancelled = true; };
    }, []);
    return codes;
}

/**
 * The menu itself. `codes` is injected so a caller that already holds them does not refetch;
 * omit it and the component sources them from the shared fetcher above.
 */
export default function IdeaCardRejectMenu({ codes, disabled, onPick, align = 'start', heading }) {
    const fetched = useIdeaCardRejectCodes();
    const list = codes && codes.length ? codes : fetched;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    className={`gap-1.5 text-base font-semibold shadow-sm disabled:opacity-60 ${INTENT_TO_BUTTON.negative}`}
                    style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                    aria-label={IDEA_QUEUE_LABELS.actReject}
                >
                    <X weight="bold" className="w-4 h-4" /> {IDEA_QUEUE_LABELS.actReject}{' '}
                    <CaretDown weight="bold" className="w-3 h-3" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align={align}
                className="z-[100] bg-white border border-slate-200 shadow-lg rounded-md w-80 max-h-[28rem] overflow-y-auto"
            >
                <DropdownMenuLabel className="text-sm font-bold uppercase tracking-wide text-slate-500">
                    {heading || IDEA_QUEUE_LABELS.rejectMenuHeading}
                </DropdownMenuLabel>
                {(list || []).map((code) => (
                    <DropdownMenuItem
                        key={code.id}
                        data-testid={`idea-reject-code-${code.id}`}
                        className="flex flex-col items-start gap-0.5 py-3 cursor-pointer"
                        onSelect={() => onPick(code.id)}
                    >
                        <span className="text-base font-semibold text-slate-800">{code.label}</span>
                        {code.definition_line_1 && (
                            <span className="text-sm text-slate-600 leading-snug">{code.definition_line_1}</span>
                        )}
                        {code.definition_line_2 && (
                            <span className="text-sm text-slate-600 leading-snug">{code.definition_line_2}</span>
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
